import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SnackbarProvider, closeSnackbar } from "notistack";
import { X } from "lucide-react";
import React, { Suspense, useMemo } from "react";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { PeriodProvider } from "./context/PeriodContext";
import RequireAuth from "./components/RequireAuth";
import AuthenticatedLayout from "./components/AuthenticatedLayout";
import RequireSuperAdmin from "./components/RequireSuperAdmin";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingSpinner from "./components/LoadingSpinner";
import DashboardRouter from "./components/DashboardRouter";
import Login from "./pages/Login";
import Assignments from "./pages/Assignments";
import AssignmentForm from "./pages/AssignmentForm";
import ExteriorInspection from "./pages/ExteriorInspection";
import CleanlinessInspection from "./pages/CleanlinessInspection";
import Checklist from "./pages/Checklist";
import Signatures from "./pages/Signatures";
import Debriefing from "./pages/Debriefing";
import AssignmentsHistory from "./pages/AssignmentsHistory";
import FuelCardsPage from "./pages/FuelCards/FuelCardsPage";
import SuperAdminLayout from "./pages/super-admin/SuperAdminLayout";
import RolesPage from "./pages/super-admin/RolesPage";
import UsersPage from "./pages/super-admin/UsersPage";

// Lazy loading pour les gros composants
const Chauffeurs = React.lazy(() => import("./pages/Chauffeurs"));
const ImportDrivers = React.lazy(() => import("./pages/ImportDrivers")); // ✨ Import massif chauffeurs
const VehiclesPage = React.lazy(() => import("./pages/Vehicles"));
const ImportVehicles = React.lazy(() => import("./pages/ImportVehicles")); // ✨ Import massif véhicules
const VehicleHistory = React.lazy(() => import("./pages/VehicleHistory"));
const VehicleDamagesPage = React.lazy(() => import("./pages/VehicleDamagesPage"));
const VehiclesHistory = React.lazy(() => import("./pages/VehiclesHistory"));
const DriverHistory = React.lazy(() => import("./pages/DriverHistory"));
const FinesAndDamages = React.lazy(() => import("./pages/FinesAndDamages"));
const Garage = React.lazy(() => import("./pages/Garage"));
const GaragesPage = React.lazy(() => import("./pages/GaragesPage"));
const ImportData = React.lazy(() => import("./pages/ImportData"));
const HandlingEquipment = React.lazy(() => import("./pages/HandlingEquipment"));
const VehicleLibrary = React.lazy(() => import("./pages/VehicleLibrary"));
const VehicleTemplateCreate = React.lazy(() => import("./pages/VehicleTemplateCreate"));
const VehicleDocuments = React.lazy(() => import("./pages/VehicleDocuments"));
const VehicleKeys = React.lazy(() => import("./pages/VehicleKeys"));
const KeyTracking = React.lazy(() => import("./pages/KeyTracking"));
const DocumentInventory = React.lazy(() => import("./pages/DocumentInventory"));

// Création du client React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Composant interne qui utilise le theme context
const AppContent = () => {
  const { isDark } = useTheme();

  // Création du thème Material-UI basé sur le mode
  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? "dark" : "light",
          primary: {
            main: "#3b82f6",
            light: "#60a5fa",
            dark: "#2563eb",
          },
          secondary: {
            main: "#c026d3",
            light: "#d946ef",
            dark: "#a21caf",
          },
          success: {
            main: "#10b981",
            light: "#34d399",
            dark: "#059669",
          },
          warning: {
            main: "#f59e0b",
            light: "#fbbf24",
            dark: "#d97706",
          },
          error: {
            main: "#ef4444",
            light: "#f87171",
            dark: "#dc2626",
          },
          background: {
            default: isDark ? "#111827" : "#f9fafb",
            paper: isDark ? "#1f2937" : "#ffffff",
          },
          text: {
            primary: isDark ? "#f3f4f6" : "#1f2937",
            secondary: isDark ? "#9ca3af" : "#6b7280",
          },
        },
        typography: {
          fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        },
        shape: {
          borderRadius: 12,
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                color: isDark ? "#f3f4f6" : "#1f2937",
              },
            },
          },
          MuiTypography: {
            styleOverrides: {
              root: {
                color: isDark ? "#f3f4f6" : "#1f2937",
              },
              h1: { color: isDark ? "#f9fafb" : "#111827" },
              h2: { color: isDark ? "#f9fafb" : "#111827" },
              h3: { color: isDark ? "#f9fafb" : "#111827" },
              h4: { color: isDark ? "#f9fafb" : "#111827" },
              h5: { color: isDark ? "#f9fafb" : "#111827" },
              h6: { color: isDark ? "#f9fafb" : "#111827" },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 12,
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 16,
                backgroundColor: isDark ? "#1f2937" : "#ffffff",
                boxShadow: isDark
                  ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -2px rgba(0, 0, 0, 0.2)"
                  : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                backgroundColor: isDark ? "#1f2937" : "#ffffff",
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                color: isDark ? "#d1d5db" : "#374151",
              },
              head: {
                backgroundColor: isDark ? "#111827" : "#f8fafc",
                color: isDark ? "#f3f4f6" : "#1f2937",
                fontWeight: 700,
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                color: isDark ? "#9ca3af" : "#4b5563",
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                color: isDark ? "#f3f4f6" : "#1f2937",
              },
              notchedOutline: {
                borderColor: isDark ? "#4b5563" : "#d1d5db",
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                "& .MuiOutlinedInput-root": {
                  borderRadius: 12,
                },
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                borderRadius: 16,
                backgroundColor: isDark ? "#1f2937" : "#ffffff",
              },
            },
          },
          MuiDialogTitle: {
            styleOverrides: {
              root: {
                color: isDark ? "#f3f4f6" : "#1f2937",
              },
            },
          },
          MuiDialogContent: {
            styleOverrides: {
              root: {
                color: isDark ? "#d1d5db" : "#374151",
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                color: isDark ? "#9ca3af" : "#4b5563",
                "&.Mui-selected": {
                  color: "#3b82f6",
                },
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                fontWeight: 500,
              },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                color: isDark ? "#d1d5db" : "#374151",
              },
            },
          },
          MuiListItemText: {
            styleOverrides: {
              primary: {
                color: isDark ? "#f3f4f6" : "#1f2937",
              },
              secondary: {
                color: isDark ? "#9ca3af" : "#6b7280",
              },
            },
          },
        },
      }),
    [isDark]
  );

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        autoHideDuration={5000}
        preventDuplicate
        action={(key) => (
          <button
            onClick={() => closeSnackbar(key)}
            className="inline-flex items-center justify-center w-6 h-6 text-white hover:bg-white hover:bg-opacity-20 rounded transition-colors"
            aria-label="fermer"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      >
        <Router>
          <ErrorBoundary>
            <Routes>
              {/* Route publique - Login */}
              <Route path="/login" element={<Login />} />

              {/* Routes protégées - Nécessitent authentification */}
              <Route
                path="/"
                element={
                  <AuthenticatedLayout>
                    <DashboardRouter />
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/super-admin"
                element={
                  <AuthenticatedLayout>
                    <RequireSuperAdmin>
                      <SuperAdminLayout />
                    </RequireSuperAdmin>
                  </AuthenticatedLayout>
                }
              >
                <Route index element={<RolesPage />} />
                <Route path="roles" element={<RolesPage />} />
                <Route path="users" element={<UsersPage />} />
              </Route>
              <Route
                path="/chauffeurs"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <Chauffeurs />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/chauffeurs/import"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <ImportDrivers />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/vehicules"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <VehiclesPage />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/vehicules/import"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <ImportVehicles />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/historique-vehicules"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <VehiclesHistory />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/vehicules/historique/:plate"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <VehicleHistory />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/vehicules/historique/:plate/degats"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <VehicleDamagesPage />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/vehicules/:plate/documents"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <VehicleDocuments />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/vehicules/:plate/cles"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <VehicleKeys />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/assignments"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <Assignments />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/assignments/history"
                element={
                  <AuthenticatedLayout>
                    <AssignmentsHistory />
                  </AuthenticatedLayout>
                }
              />
              <Route path="/assignments/new" element={<AuthenticatedLayout><AssignmentForm /></AuthenticatedLayout>} />
              <Route path="/inspection-exterieure/:id" element={<AuthenticatedLayout><ExteriorInspection /></AuthenticatedLayout>} />
              <Route path="/inspection-proprete/:id" element={<AuthenticatedLayout><CleanlinessInspection /></AuthenticatedLayout>} />
              <Route path="/checklist/:id" element={<AuthenticatedLayout><Checklist /></AuthenticatedLayout>} />
              <Route path="/suivi-cles/:id" element={<AuthenticatedLayout><Suspense fallback={<LoadingSpinner />}><KeyTracking /></Suspense></AuthenticatedLayout>} />
              <Route path="/inventaire-documents/:id" element={<AuthenticatedLayout><Suspense fallback={<LoadingSpinner />}><DocumentInventory /></Suspense></AuthenticatedLayout>} />
              <Route path="/signatures/:id" element={<AuthenticatedLayout><Signatures /></AuthenticatedLayout>} />
              <Route path="/debriefing/:id" element={<AuthenticatedLayout><Debriefing /></AuthenticatedLayout>} />
              <Route
                path="/vehicules/historique/:plate/processus/:processId"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <VehicleHistory />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/chauffeurs/historique/:id"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <DriverHistory />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/cartes-carburant"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <FuelCardsPage />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/amendes-degats"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <FinesAndDamages />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/garage"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <Garage />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/garages"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <GaragesPage />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/import"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <ImportData />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/vehicle-library"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <VehicleLibrary />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/vehicle-template/create"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <VehicleTemplateCreate />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
              <Route
                path="/equipements-manutention"
                element={
                  <AuthenticatedLayout>
                    <Suspense fallback={<LoadingSpinner />}>
                      <HandlingEquipment />
                    </Suspense>
                  </AuthenticatedLayout>
                }
              />
            </Routes>
          </ErrorBoundary>
        </Router>
      </SnackbarProvider>
    </MuiThemeProvider>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PeriodProvider>
          <ThemeProvider>
            <AppContent />
          </ThemeProvider>
        </PeriodProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
